import axios from "axios";
import { getCache, putCache, outboxCount } from "./offline/store";
import { applyOverlay } from "./offline/overlay";
import { handleOfflineMutation, readKey, startAutoSync } from "./offline/sync";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---------- Modo sin conexión ----------
const pathOf = (url) => (url || "").split("?")[0];

const OFFLINE_GET = [
  /^\/patients$/,
  /^\/patients\/[^/]+$/,
  /^\/patients\/[^/]+\/(consultations|prescriptions|attachments|growth|appointments|vaccination|pending-vitals|odontogram|perio|ortho)$/,
  /^\/consultations\/[^/]+\/versions$/,
  /^\/saved-drugs/,
  /^\/drugs(\/|$)/,
  /^\/doctor\/me$/,
  /^\/auth\/me$/,
  /^\/compliance\/summary$/,
];
const isOfflineGet = (p) => OFFLINE_GET.some((re) => re.test(p));
const isListPath = (p) => /^\/patients$/.test(p) || /\/(consultations|prescriptions|attachments)$/.test(p);

function isOfflineMutation(method, p) {
  if (method === "post" && (p === "/patients" || p === "/consultations" || p === "/prescriptions" || p === "/audit/log")) return true;
  if (method === "post" && /^\/patients\/[^/]+\/(attachments|lab-note)$/.test(p)) return true;
  if (method === "put" && /^\/(patients|consultations)\/[^/]+$/.test(p)) return true;
  if (method === "delete" && /^\/(patients\/[^/]+|attachments\/[^/]+)$/.test(p)) return true;
  return false;
}

api.interceptors.response.use(
  async (res) => {
    try {
      const cfg = res.config || {};
      const method = (cfg.method || "get").toLowerCase();
      if (method === "get" && cfg.responseType !== "blob" && isOfflineGet(pathOf(cfg.url))) {
        await putCache(readKey(cfg.url), res.data);
        if ((await outboxCount()) > 0) res.data = await applyOverlay(cfg.url, res.data);
      }
    } catch { /* la caché offline es best-effort */ }
    return res;
  },
  async (err) => {
    const cfg = err?.config || {};
    const method = (cfg.method || "get").toLowerCase();
    const p = pathOf(cfg.url);

    // Sin conexión: los errores de red no traen `response`.
    if (!err.response && cfg.url) {
      if (method === "get" && cfg.responseType !== "blob" && isOfflineGet(p)) {
        try {
          const cached = await getCache(readKey(cfg.url));
          const seed = cached != null ? cached : (isListPath(p) ? [] : null);
          const data = await applyOverlay(cfg.url, seed);
          if (data != null) {
            return { data, status: 200, statusText: "OK (offline)", headers: {}, config: cfg, _offline: true };
          }
        } catch { /* cae al reject */ }
      }
      if (isOfflineMutation(method, p)) {
        try {
          const data = await handleOfflineMutation(cfg);
          if (data != null) {
            return { data, status: 200, statusText: "Guardado (offline)", headers: {}, config: cfg, _offline: true };
          }
        } catch { /* cae al reject */ }
      }
      return Promise.reject(err);
    }

    const status = err?.response?.status;
    if (status === 402 && !window.location.pathname.startsWith("/receta")) {
      if (!window.__license_reloaded) {
        window.__license_reloaded = true;
        window.location.reload();
      }
    }
    // Sesión invalidada (otro dispositivo inició sesión → "el último gana").
    const PUBLIC_PREFIXES = ["/", "/login", "/receta", "/directorio", "/privacidad", "/pago", "/asistente", "/enfermeria"];
    if (
      status === 401 &&
      localStorage.getItem("session_token") &&
      window.location.pathname !== "/" &&
      !PUBLIC_PREFIXES.some((pre) => pre !== "/" && window.location.pathname.startsWith(pre))
    ) {
      localStorage.removeItem("session_token");
      localStorage.setItem("session_ended", "other_device");
      window.location.replace("/login");
    }
    return Promise.reject(err);
  }
);

startAutoSync();
